/**
 * ZEEVAAH Phase 5.1 — desktop header dropdown stability.
 * SHOP and COLLECTIONS act as dropdown triggers on desktop; child links handle navigation.
 */
(() => {
  const CLOSE_DELAY_MS = 280;
  const desktopMenu = window.matchMedia('(min-width: 750px)');
  const TRIGGER_PATHS = new Set(['/collections/all', '/collections']);

  const getPath = (link) => {
    try {
      return new URL(link.href, window.location.origin).pathname.replace(/\/+$/, '') || '/';
    } catch (_) {
      return '';
    }
  };

  const getPathElement = (event, predicate) =>
    event.composedPath().find((node) => node instanceof Element && predicate(node));

  const getParentMenuLink = (event) =>
    getPathElement(
      event,
      (node) =>
        node instanceof HTMLAnchorElement &&
        node.matches('[ref="menuitem"][aria-haspopup="true"]')
    );

  const ensureViewAllCollectionsLink = (headerMenu) => {
    if (!desktopMenu.matches || !(headerMenu instanceof Element)) return;

    headerMenu.querySelectorAll('[ref="menuitem"][aria-haspopup="true"]').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement) || getPath(link) !== '/collections') return;

      const item = link.closest('.menu-list__list-item');
      if (!(item instanceof HTMLElement)) return;

      const list =
        item.querySelector('[ref="submenu[]"] .mega-menu__list') ||
        item.querySelector('.menu-list__submenu .mega-menu__list');
      if (!(list instanceof HTMLElement) || list.querySelector('[data-zv-view-all-collections]')) return;

      const firstColumn = list.querySelector(':scope > .mega-menu__column') || list.firstElementChild;
      if (!(firstColumn instanceof HTMLElement)) return;

      const wrapper = document.createElement('div');
      wrapper.dataset.zvViewAllCollections = '';

      const viewAll = document.createElement('a');
      viewAll.href = '/collections';
      viewAll.className = 'mega-menu__link';
      viewAll.setAttribute('aria-label', 'View All Collections');

      const label = document.createElement('span');
      label.className = 'wrap-text';
      label.textContent = 'View All Collections';

      viewAll.append(label);
      wrapper.append(viewAll);
      firstColumn.prepend(wrapper);
    });
  };

  customElements.whenDefined('header-menu').then(() => {
    const HeaderMenu = customElements.get('header-menu');
    if (!HeaderMenu || HeaderMenu.prototype.__zvStableDropdownPatched) return;

    const originalDeactivate = HeaderMenu.prototype.deactivate;
    if (typeof originalDeactivate === 'function') {
      HeaderMenu.prototype.deactivate = function (event) {
        if (!desktopMenu.matches || event?.type !== 'pointerleave') {
          return originalDeactivate.call(this, event);
        }

        if (this.__zvMenuCloseTimer) {
          window.clearTimeout(this.__zvMenuCloseTimer);
        }

        const target = event.target;
        const relatedTarget = event.relatedTarget;

        this.__zvMenuCloseTimer = window.setTimeout(() => {
          this.__zvMenuCloseTimer = null;

          if (this.matches(':hover') || this.matches(':focus-within')) return;

          originalDeactivate.call(this, {
            target,
            relatedTarget,
            type: 'pointerleave',
          });
        }, CLOSE_DELAY_MS);
      };
    }

    Object.defineProperty(HeaderMenu.prototype, '__zvStableDropdownPatched', {
      value: true,
      configurable: true,
    });

    document.querySelectorAll('header-menu').forEach((headerMenu) => {
      ensureViewAllCollectionsLink(headerMenu);

      const observer = new MutationObserver(() => {
        window.requestAnimationFrame(() => ensureViewAllCollectionsLink(headerMenu));
      });
      observer.observe(headerMenu, { childList: true, subtree: true });
    });
  });

  document.addEventListener(
    'click',
    (event) => {
      if (!desktopMenu.matches) return;

      if (
        event instanceof MouseEvent &&
        (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      ) {
        return;
      }

      const link = getParentMenuLink(event);
      if (!(link instanceof HTMLAnchorElement)) return;

      const path = getPath(link);
      if (!TRIGGER_PATHS.has(path)) return;

      const item = link.closest('.menu-list__list-item');
      const headerMenu =
        getPathElement(event, (node) => node.localName === 'header-menu') || link.closest('header-menu');

      if (!(item instanceof HTMLElement) || !(headerMenu instanceof HTMLElement)) return;

      const directMenuLink = item.querySelector(':scope > [ref="menuitem"]');
      if (directMenuLink !== link) return;

      event.preventDefault();

      if (path === '/collections') {
        ensureViewAllCollectionsLink(headerMenu);
      }

      if (typeof headerMenu.activate === 'function') {
        headerMenu.activate({ target: item });
      }
    },
    true
  );
})();